/**
 * @fileoverview Table listing MCP tokens with a per-row revoke action.
 * @module features/mcp/components/McpTokenList
 */

import type { ReactElement } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { McpToken } from '../schemas/mcpToken'

interface McpTokenListProps {
  tokens: McpToken[]
  isLoading: boolean
  onRevoke: (id: string) => void
  revokingId: string | null
}

type TokenStatus = 'active' | 'revoked' | 'expired'

function tokenStatus(token: McpToken): TokenStatus {
  if (token.revokedAt != null) return 'revoked'
  if (new Date(token.expiresAt).getTime() < Date.now()) return 'expired'
  return 'active'
}

function formatDate(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

function formatDateTime(value: string | null): string {
  if (value == null) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

/**
 * Presentational list of MCP tokens with revoke action per row.
 */
export function McpTokenList({
  tokens,
  isLoading,
  onRevoke,
  revokingId,
}: McpTokenListProps): ReactElement {
  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="mcp-tokens-loading">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    )
  }

  if (tokens.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No tokens yet. Generate one to connect Claude.
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Created</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tokens.map((token) => {
          const status = tokenStatus(token)
          const canRevoke = status === 'active'
          return (
            <TableRow key={token.id}>
              <TableCell className="font-medium">{token.name ?? '—'}</TableCell>
              <TableCell>{formatDate(token.createdAt)}</TableCell>
              <TableCell>{formatDateTime(token.lastUsedAt)}</TableCell>
              <TableCell>{formatDate(token.expiresAt)}</TableCell>
              <TableCell>
                <Badge
                  variant={
                    status === 'active'
                      ? 'default'
                      : status === 'expired'
                        ? 'secondary'
                        : 'destructive'
                  }
                >
                  {status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {canRevoke ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRevoke(token.id)}
                    disabled={revokingId === token.id}
                  >
                    {revokingId === token.id ? 'Revoking…' : 'Revoke'}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
